import { OrderType } from '@sg/graphql-schema';

import {
  useFeedbackInStoreOrderStatusQuery,
  useFeedbackOrderStatusQuery,
} from '../../GraphQL/RateOrder.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useDerivedRateOrderStatus = (orderId: string, orderTypeIsInStore?: boolean) => {
  const [digitalResponse] = useFeedbackOrderStatusQuery({
    variables: { orderId },
    requestPolicy: 'network-only',
    pause: !orderId || orderTypeIsInStore,
  });

  const [inStoreResponse] = useFeedbackInStoreOrderStatusQuery({
    variables: { orderId },
    requestPolicy: 'network-only',
    pause: !orderId || !orderTypeIsInStore,
  });

  const digitalOrderStatus = digitalResponse.data?.orderStatus;
  const isValidDigitalOrder = digitalOrderStatus?.__typename === 'OrderStatus';
  const digitalOrder = isValidDigitalOrder ? digitalOrderStatus.order : undefined;
  const flattenedOrderStatus = isValidDigitalOrder
    ? digitalOrderStatus.flattenedOrderStatus
    : undefined;

  const inStoreOrder = inStoreResponse.data?.inStoreLoyaltyOrder ?? undefined;
  const hasInStoreOrder = !!inStoreOrder;

  const isFetching = digitalResponse.fetching || inStoreResponse.fetching;

  const isCompletedOrder = flattenedOrderStatus === 'completed';
  const isCanceledOrder = flattenedOrderStatus === 'failed';
  const hasPendingFeedback = digitalOrder?.isPendingFeedback === true;

  const digitalOrderFinal = digitalOrder
    ? {
        id: digitalOrder.id,
        orderType: digitalOrder.orderType,
        wantedTime: digitalOrder.wantedTime,
        isPendingFeedback: digitalOrder.isPendingFeedback,
        restaurantEntityId: digitalOrder.restaurant.entityId ?? undefined,
        isOutpost: digitalOrder.restaurant.isOutpost,
      }
    : undefined;

  const order = hasInStoreOrder
    ? {
        id: inStoreOrder!.id,
        orderType: OrderType.InStore,
        wantedTime: inStoreOrder!.wantedTime,
        isPendingFeedback: inStoreOrder!.isPendingFeedback,
        restaurantEntityId: inStoreOrder!.restaurant.entityId,
        isOutpost: false,
      }
    : digitalOrderFinal;

  const isInvalidOrder =
    !isFetching &&
    !hasInStoreOrder &&
    (!isValidDigitalOrder || (isValidDigitalOrder && isCanceledOrder));

  const isPendingOrder =
    !hasInStoreOrder && isValidDigitalOrder && !isCanceledOrder && !isCompletedOrder;

  const isRatedOrder =
    !hasInStoreOrder && isValidDigitalOrder && isCompletedOrder && !hasPendingFeedback;

  return {
    isFetching,
    isInvalidOrder,
    isPendingOrder,
    isRatedOrder,
    order,
  };
};
